import {createStyles, Text} from '@mantine/core';
import React from 'react';

import {AnalyticsMetadataForContext} from 'analytics/AnalyticsTypes';
import ArchiveOrUnarchiveButton from 'archive/components/buttons/ArchiveOrUnarchiveButton';
import * as ArchiveModal from 'archive/components/modals/ArchiveModal';
import coreObjectIsArchivable from 'archive/isArchivable';
import isArchived from 'archive/isArchived';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ConnectionStatus, ConnectionStatusResponse} from 'instruments/connections/ConnectionConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

import ConnectionCollaboratorsModal from './ConnectionCollaboratorsModal';
import RefreshConnectionStatusButton from './RefreshConnectionStatusButton';

const EXPORT_TABLE_TO_DATASET_TEXT = 'Export table to dataset';
const MORE_ACTIONS_TEXT = 'More actions';
const REFRESH_UNAVAILABLE_ARCHIVED_TEXT = 'Unarchive the connection to check its status';
const REFRESH_UNAVAILABLE_DISABLED_TEXT = 'Enable the connection to check its status';
const REFRESH_UNAVAILABLE_NO_EDIT_TEXT = "You don't have permission to check this connection's status";

const useStyles = createStyles((theme) => ({
  destructiveIcon: {
    // Doubled selector to override the menu's default option icon color.
    '&&': {
      color: theme.colors.red[8],
    },
  },
}));

export interface ConnectionHeaderActionsProps {
  connectionId: string;
  connection: ArchiveModal.ArchiveModalItem;
  isArchivable: boolean;
  isUnarchivable: boolean;
  status: ConnectionStatusResponse | null;
  onArchive: (ids: Array<string>, purpose: string) => void;
  onUnarchive: (ids: Array<string>) => void;
  onUpdateIsEnabled: (isEnabled: boolean) => void;
  onRefreshStatus: () => void;
  /** Opens the Snowflake or Databricks table export; the export button only shows when this is set. */
  onExportTableToDataset?: () => void;
  getAnalyticsMetadata: () => AnalyticsMetadataForContext;
}

/**
 * The buttons in a connection's page header: refresh the status, export a table to a dataset (Snowflake and Databricks), and a
 * More actions menu for the audit log, settings, disabling and archiving.
 *
 * Unarchive stays an inline button rather than a menu item, as on other object pages.
 */
export default function ConnectionHeaderActions(props: ConnectionHeaderActionsProps): JSX.Element {
  const {classes} = useStyles();
  const isDisabled = props.status?.value === ConnectionStatus.DISABLED;
  const refreshDisabledReason = isArchived(props.connection)
    ? REFRESH_UNAVAILABLE_ARCHIVED_TEXT
    : isDisabled
      ? REFRESH_UNAVAILABLE_DISABLED_TEXT
      : !isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_EDIT})
        ? REFRESH_UNAVAILABLE_NO_EDIT_TEXT
        : undefined;
  const canArchive =
    !isArchived(props.connection) && coreObjectIsArchivable(props.connection) && props.isArchivable;

  const statusOptions = [
    <Option
      key="enable-or-disable"
      icon={isDisabled ? 'lock-open' : 'lock'}
      onSelect={() => {
        props.onUpdateIsEnabled(isDisabled);
      }}
    >
      {isDisabled ? 'Enable connection' : 'Disable connection'}
    </Option>,
  ];
  if (canArchive) {
    statusOptions.push(
      <Option
        key="archive"
        icon="archive"
        iconClassName={classes.destructiveIcon}
        onSelect={() => {
          ArchiveModal.open({
            items: [props.connection],
            onArchive: props.onArchive,
            analyticsMetadata: props.getAnalyticsMetadata(),
          });
        }}
      >
        {/* Archive is destructive, so it's styled red, like Archive in other More menus. */}
        <Text span={true} c="red.8">
          Archive connection
        </Text>
      </Option>
    );
  }

  return (
    <>
      {isArchived(props.connection) && (
        <ArchiveOrUnarchiveButton
          className="metadata-header-button-spacing-left"
          isUnarchivable={props.isUnarchivable}
          item={props.connection}
          getAnalyticsMetadata={props.getAnalyticsMetadata}
          onArchive={props.onArchive}
          onUnarchive={props.onUnarchive}
        />
      )}
      <RefreshConnectionStatusButton
        connectionId={props.connectionId}
        disabledReason={refreshDisabledReason}
        onRefresh={props.onRefreshStatus}
      />
      {props.onExportTableToDataset && (
        <Tooltip text={EXPORT_TABLE_TO_DATASET_TEXT} disableContentSelection={true}>
          <Button
            aria-label={EXPORT_TABLE_TO_DATASET_TEXT}
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            className="metadata-header-button-spacing-left"
            icon="share-v2"
            onClick={props.onExportTableToDataset}
            size={ButtonSize.SMALL}
          />
        </Tooltip>
      )}
      <Dropdown
        menu={
          <Menu dropLeft={true} height={MenuHeight.MEDIUM} width={MenuWidth.FIT_CONTENT}>
            <Section>
              <Option
                icon="audit-trail"
                onSelect={() => {
                  exportAuditLogs([props.connectionId], {});
                }}
              >
                {EXPORT_AUDIT_LOG_DISPLAY_STRING}
              </Option>
              <Option
                icon="settings"
                onSelect={() => {
                  legacyRunModal(ConnectionCollaboratorsModal, {connectionId: props.connectionId});
                }}
              >
                Connection settings
              </Option>
            </Section>
            {...statusOptions}
          </Menu>
        }
        tooltip={MORE_ACTIONS_TEXT}
      >
        <Button
          aria-label={MORE_ACTIONS_TEXT}
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          className="metadata-header-button-spacing-left"
          icon="overflow"
          size={ButtonSize.SMALL}
        />
      </Dropdown>
    </>
  );
}
